import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Stethoscope, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { VirtualizedDoctorList } from '@/components/common/VirtualizedDoctorList';
import { getDoctorAnalytics, listDoctors, type DoctorDTO } from '@/services/doctors';
import Badge from '@/components/common/Badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Página de Busca de Médicos (Dashboard do Paciente)
 * Implementa filtros por especialidade, busca textual e listagem virtualizada
 */
export default function BuscarMedicosPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, logout } = useAuth();
  
  const [doctors, setDoctors] = useState<DoctorDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if auth is complete and user is patient
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'PATIENT') {
      navigate('/dashboard');
      return;
    }

    const fetchDoctors = async () => {
      try {
        setIsLoading(true);
        // Pass onlyEligible = true (4th argument)
        const response = await listDoctors(undefined, undefined, undefined, true);
        // Assume API format returns success/data
        if (response.success && Array.isArray(response.data)) {
          // Apenas médicos aprovados
          const approvedDoctors = response.data.filter((doctor) => doctor.isApproved);
          const doctorsWithAnalytics = await Promise.all(
            approvedDoctors.map(async (doctor) => {
              const analyticsResponse = await getDoctorAnalytics(doctor.id);
              if (analyticsResponse.success && analyticsResponse.data) {
                return { ...doctor, analytics: analyticsResponse.data };
              }
              return doctor;
            }),
          );
          setDoctors(doctorsWithAnalytics);
        }
      } catch (error) {
        console.error('Failed to fetch doctors:', error);
        toast.error('Erro ao carregar a lista de médicos.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoctors();
  }, [user, authLoading, navigate]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      toast.error('Erro ao sair');
    }
  };

  // Get unique specialties
  const specialties = useMemo(() => {
    const specs = Array.isArray(doctors)
      ? doctors.map(d => d.specialty).filter(Boolean)
      : [];
    return Array.from(new Set(specs)).sort();
  }, [doctors]);

  // Filter doctors based on inputs
  const filteredDoctors = useMemo(() => {
    return doctors.filter(doctor => {
      const matchSearch = 
        !searchQuery || 
        doctor.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        doctor.specialty?.toLowerCase().includes(searchQuery.toLowerCase());
        
      const matchSpecialty = 
        !selectedSpecialty || 
        doctor.specialty === selectedSpecialty;

      return matchSearch && matchSpecialty;
    });
  }, [doctors, searchQuery, selectedSpecialty]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSpecialty = useCallback(() => {
    setSelectedSpecialty(null);
  }, []);

  const handleSelectSpecialty = useCallback((spec: string) => () => {
    setSelectedSpecialty(spec);
  }, []);

  const handleSelectDoctor = useCallback((doc: DoctorDTO) => {
    navigate(`/medico/${doc.id}`);
  }, [navigate]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex-shrink-0 flex items-center justify-between z-10">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Buscar Médicos
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Encontre o especialista ideal para você.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
          </div>
        </header>

        {/* Filters Section */}
        <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-4 flex-shrink-0 z-10 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar por nome ou especialidade..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 h-11 bg-gray-50 dark:bg-neutral-800 border-transparent dark:border-transparent focus:border-teal-500 rounded-xl"
            />
          </div>

          <div className="flex-1 overflow-x-auto pb-2 md:pb-0 scrollbar-hide flex items-center gap-2">
            <div
              className={cn(
                "cursor-pointer whitespace-nowrap h-9 px-4 rounded-full flex items-center justify-center text-sm font-medium transition-colors border",
                selectedSpecialty === null 
                  ? "bg-teal-600 text-white border-teal-600" 
                  : "bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700"
              )}
              onClick={handleClearSpecialty}
            >
              Todos
            </div>
            {specialties.map(spec => (
              <div
                key={spec}
                className={cn(
                  "cursor-pointer whitespace-nowrap h-9 px-4 rounded-full flex gap-2 items-center justify-center text-sm font-medium transition-colors border",
                  selectedSpecialty === spec
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700"
                )}
                onClick={handleSelectSpecialty(spec)}
              >
                <Stethoscope className="w-3.5 h-3.5" />
                {spec}
              </div>
            ))}
          </div>
        </div>

        {/* Content with Virtualization */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          <VirtualizedDoctorList
            doctors={filteredDoctors}
            isLoading={isLoading}
            onSelectDoctor={handleSelectDoctor}
          />
        </main>
      </div>
    </div>
  );
}
